/**
 * Tickets a person just created that must stay on screen until AppSheet's real ticket is read back.
 *
 * WHY. A fast-saved ticket is first a stand-in doc (`oh_<hash>`); when AppSheet issues the number the server swaps it, in ONE
 * transaction, for the real doc and deletes the stand-in (serviceLogAddSwap.ts). A single read cannot return "neither" from timing
 * alone, and the real doc carries the stand-in's Stage on both swap branches, so the open-list Stage predicate is not the explanation
 * either. What IS observed (2026-10-30, S261030-ESH): the row spun and then disappeared at the swap; the mechanism that hid the real
 * doc from the screen is NOT verified (no prod read was made by this change). So this is a safety net, not a diagnosis: after a queued
 * create we REMEMBER the row here and re-add it to every list read that carries neither the stand-in nor the real doc, until a row
 * with `_standInId === <stand-in id>` is observed in the raw (unfiltered) rows of a read.
 *
 * Memory only (module state): the stand-in itself is durable on the server; this only bridges the swap within one tab. A reload or a
 * second tab starts with no holds. Cleared on logout and on a user change (AuthContext).
 */
export const HELD_CREATE_MAX_AGE_MS = 45 * 60_000;

export interface HeldCreate {
  readonly standInId: string;
  readonly generatorId: string;
  readonly heldAtMs: number;
  /** Row for the open-ticket lists (a MergedServiceTicket). */
  readonly ticket: unknown;
  /** Row for the generator page's Ticket History (a ServiceLogEntry). */
  readonly historyEntry: unknown;
}

/** How a list names its rows: the doc id, and (on a real ticket made from a stand-in) that stand-in's id. */
export interface HeldRowAccess<TRow> {
  readonly idOf: (row: TRow) => string;
  readonly standInIdOf: (row: TRow) => string | undefined;
  readonly heldRowOf: (held: HeldCreate) => TRow;
  /** Only held tickets of this generator (the generator page); omitted = all (the open lists). */
  readonly generatorId?: string;
}

let held: ReadonlyArray<HeldCreate> = [];

export function holdCreate(entry: HeldCreate): void {
  held = [...held.filter((h) => h.standInId !== entry.standInId), entry];
}

export function releaseHeldCreate(standInId: string): void {
  held = held.filter((h) => h.standInId !== standInId);
}

export function heldCreatesFor(generatorId?: string): ReadonlyArray<HeldCreate> {
  return generatorId === undefined ? held : held.filter((h) => h.generatorId === generatorId);
}

/**
 * Release every hold whose real ticket appears among the RAW rows of a read (before any open/archive/soft-delete filter), so a real
 * doc a list then hides (cancelled at once, archived) still ends the hold instead of re-vanishing when it expires.
 * NOTE: a hold released here is no longer re-added by another view's merge; a view whose cache still holds the baked-in held row
 * self-heals on its next poll (that row is `pendingCreate: 'sending'`, which keeps both lists polling).
 */
export function releaseObservedCreates(rows: ReadonlyArray<{ readonly id: string; readonly standInId?: unknown }>): void {
  for (const r of rows) {
    if (typeof r.standInId === 'string' && r.standInId && r.standInId !== r.id) releaseHeldCreate(r.standInId);
  }
}

export function clearHeldCreates(): void {
  held = [];
}

/**
 * Add back every held ticket the fetched list does not carry, and release the ones whose real ticket is in it.
 *  - the stand-in doc is in the list: the server copy shows it; keep holding (the swap has not happened).
 *  - a real doc naming the stand-in is in the list: the swap is observed; release.
 *  - neither: the swap is in flight; show the held row (a copy, `list` is never mutated).
 * Entries older than {@link HELD_CREATE_MAX_AGE_MS} are dropped (the shared stand-in limit takes over).
 */
export function mergeHeldCreates<TRow>(list: readonly TRow[], access: HeldRowAccess<TRow>, nowMs: number = Date.now()): TRow[] {
  const extra: TRow[] = [];
  for (const h of heldCreatesFor(access.generatorId)) {
    if (nowMs - h.heldAtMs > HELD_CREATE_MAX_AGE_MS) {
      releaseHeldCreate(h.standInId);
      continue;
    }
    if (list.some((r) => access.standInIdOf(r) === h.standInId && access.idOf(r) !== h.standInId)) {
      releaseHeldCreate(h.standInId);
      continue;
    }
    if (list.some((r) => access.idOf(r) === h.standInId)) continue;
    extra.push(access.heldRowOf(h));
  }
  return extra.length === 0 ? [...list] : [...extra, ...list];
}
